import { CheckIcon, CopyIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, type ButtonProps } from "../components/button";
import { cn } from "../lib/utils";

export interface CopyButtonProps extends Omit<ButtonProps, "onClick" | "value"> {
  /** Text placed on the clipboard. */
  value: string;
  /** Visible label; icon-only when omitted. */
  label?: string;
  copiedLabel?: string;
}

/** Copies `value` to the clipboard and flips to a check mark for 1.5 s. */
export function CopyButton({
  value,
  label,
  copiedLabel = "Copied",
  variant = "outline",
  size,
  className,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <Button
      type="button"
      variant={variant}
      size={size ?? (label ? "sm" : "icon")}
      className={cn(!label && "size-7", className)}
      aria-label={label ? undefined : `Copy ${value}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          // Clipboard unavailable (insecure context); leave the value selectable instead.
        }
      }}
      {...props}
    >
      {copied ? <CheckIcon className="text-success" /> : <CopyIcon />}
      {label ? (copied ? copiedLabel : label) : null}
    </Button>
  );
}

/** Read-only value box with a copy button (secrets, invite links, codes). */
export function CopyField({
  value,
  label,
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <code className="min-w-0 flex-1 select-all truncate rounded-md border bg-muted px-2 py-1.5 font-mono text-xs">
        {value}
      </code>
      <CopyButton value={value} label={label} />
    </div>
  );
}
